import React, { useEffect, useRef, useState } from "react";
import { BookOpen, ChevronDown, ChevronUp, CheckCircle2, XCircle, AlertTriangle, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

/** Extra Graph application permissions the CIS checks need + what could not be read in this scan. */
export default function ComplianceSetup({ required, scan, sample, focus = false }) {
  const missing = new Set(scan?.permissions?.missing || []);
  const errors = scan?.source_errors || {};
  const errorKeys = Object.keys(errors);
  const [open, setOpen] = useState(missing.size > 0 || errorKeys.length > 0);
  const granted = scan?.permissions?.granted || [];
  const ref = useRef(null);
  useEffect(() => {
    if (focus) {
      setOpen(true);
      setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [focus, scan?.id]);
  const copyMissing = () => {
    navigator.clipboard?.writeText([...missing].join("\n")).then(() => toast.success("Missing permissions copied"));
  };
  return (
    <section ref={ref} className={`ws-card glass setup-guide ${focus ? "focused" : ""}`} id="cis-setup" data-testid="cis-setup">
      <button type="button" className="setup-guide-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="cis-setup-toggle">
        <BookOpen size={15} />
        <span>
          <b>Setup guide - Graph permissions for automated checks</b>
          <small>
            {sample ? "Sample tenant: mock configuration data, no permissions needed." : missing.size ? `${missing.size} of ${required.length} permissions missing - some controls could not be evaluated.` : `${required.length} application permissions · all granted.`}
            {errorKeys.length ? ` ${errorKeys.length} source(s) failed to load.` : ""}
          </small>
        </span>
        {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>
      {open && (
        <div className="setup-guide-body">
          <p className="muted small">
            Add these as <b>application</b> permissions on the same Entra app registration used for license scans, grant admin consent, then re-run the CIS scan. Read-only; nothing is changed in your tenant.{" "}
            <a href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noreferrer" className="setup-link">
              Open app registrations <ExternalLink size={11} />
            </a>
          </p>
          {missing.size > 0 && (
            <div className="ws-alert warn cis-missing-banner" data-testid="cis-missing-summary">
              <AlertTriangle size={13} />
              <span>
                <b>{missing.size} missing:</b> {[...missing].join(", ")}
              </span>
              <button type="button" className="tb-btn sm ghost" onClick={copyMissing} data-testid="cis-missing-copy">
                <Copy size={12} /> Copy list
              </button>
            </div>
          )}
          <ul className="perm-list wide" data-testid="cis-perm-list">
            {required.map((p) => {
              const state = !scan || (sample && !missing.size) ? null : missing.has(p.name) ? false : granted.includes(p.name) || true;
              return (
                <li key={p.name} className={state === false ? "missing" : state ? "ok" : ""} data-testid={`cis-perm-${p.name}`}>
                  {state === false ? <XCircle size={14} /> : state ? <CheckCircle2 size={14} /> : <span className="dot" />}
                  <span className="perm-text">
                    <span className="mono">{p.name}</span>
                    <span className="muted">{p.why}</span>
                  </span>
                </li>
              );
            })}
          </ul>
          {errorKeys.length > 0 && (
            <div className="ws-alert warn" data-testid="cis-source-errors">
              <AlertTriangle size={13} />
              <span>
                Could not read: {errorKeys.map((k) => `${k} (${String(errors[k]).slice(0, 90)})`).join("; ")}
              </span>
            </div>
          )}
          <p className="muted small">Controls for Exchange Online, Purview, Teams, Fabric and most Defender policies have no Graph API and stay manual: follow the audit steps (PowerShell / admin portals) and record an attestation.</p>
        </div>
      )}
    </section>
  );
}
